import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../utils/api';

// Query Keys
export const messageQueryKeys = {
  all: ['messages'],
  lists: () => [...messageQueryKeys.all, 'list'],
  list: (params) => [...messageQueryKeys.lists(), params],
  detail: (userSysId, messageSysId) => [...messageQueryKeys.all, 'detail', userSysId, messageSysId],
  unreadCount: (userSysId) => [...messageQueryKeys.all, 'unreadCount', userSysId],
};

// ==================== 메시지 목록 조회 ====================
/**
 * 메시지 목록 조회
 * @param {string} userSysId - 사용자 ID
 * @param {Object} params - 쿼리 파라미터
 * @param {string} params.Title - 메시지 제목
 * @param {string} params.Type - 메시지 유형
 * @param {string} params.IsRead - 메시지 읽음 여부
 * @param {number} params.Page - 페이지 번호
 * @param {number} params.PageSize - 페이지 크기
 * @param {string} params.OrderBy - 정렬 기준
 * @param {string} params.OrderDirection - 정렬 방향 (asc, desc)
 */
export const useMessageList = (userSysId, params = {}) => {
  return useQuery({
    queryKey: messageQueryKeys.list(params),
    queryFn: async () => {
      const response = await api.get(`/drivecore/id/api/MessageBox/${userSysId}`, { params });
      return response;
    },
    enabled: !!userSysId,
  });
};

// ==================== 메시지 상세 조회 ====================
/**
 * 메시지 상세 조회
 * @param {string} userSysId - 사용자 ID
 * @param {string} messageSysId - 메시지 ID
 */
export const useMessageDetail = (userSysId, messageSysId) => {
  return useQuery({
    queryKey: messageQueryKeys.detail(userSysId, messageSysId),
    queryFn: async () => {
      const response = await api.get(`/drivecore/id/api/MessageBox/detail/${userSysId}/${messageSysId}`);
      return response;
    },
    enabled: !!userSysId && !!messageSysId,
  });
};

// ==================== 메시지 읽지 않은 개수 조회 ====================
/**
 * 메시지 읽지 않은 개수 조회
 * @param {string} userSysId - 사용자 ID
 */
export const useMessageUnreadCount = (userSysId) => {
  return useQuery({
    queryKey: messageQueryKeys.unreadCount(userSysId || 'unknown'),
    queryFn: async () => {
      const response = await api.get(`/drivecore/id/api/MessageBox/unread/${userSysId}`);
      return response;
    },
    enabled: !!userSysId,
    staleTime: 5 * 60 * 1000,
  });
};

// ==================== 메시지 읽음 처리 ====================
/**
 * 메시지 읽음 처리 (단일)
 * @param {string} userSysId - 사용자 ID
 * @param {string} messageSysId - 메시지 ID
 */
export const useUpdateMessageReadStatus = (userSysId, messageSysId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await api.put(`/drivecore/id/api/MessageBox/updatereadstatus/${userSysId}/${messageSysId}`);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(messageQueryKeys.unreadCount(userSysId));
      queryClient.invalidateQueries(messageQueryKeys.list());
    },
  });
};

// ==================== 메시지 읽음 처리 (다중) ====================
/**
 * 메시지 읽음 처리 (다중)
 * @param {string} userSysId - 사용자 ID
 */
export const useUpdateMultipleMessageReadStatus = (userSysId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (messageIds) => {
      // 여러 메시지에 대해 읽음 처리
      const promises = messageIds.map(messageId => {
        return api.put(`/drivecore/id/api/MessageBox/updatereadstatus/${userSysId}/${messageId}`);
      });
      return await Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(messageQueryKeys.unreadCount(userSysId));
      queryClient.invalidateQueries(messageQueryKeys.list());
    },
  });
};

// ==================== 메시지 삭제 ====================
/**
 * 메시지 삭제
 * @param {string} userSysId - 사용자 ID
 */
export const useDeleteMessage = (userSysId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (messageSysId) => {
      const response = await api.delete(`/drivecore/id/api/MessageBox/${userSysId}/${messageSysId}`);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messageQueryKeys.list() });
      queryClient.invalidateQueries({ queryKey: messageQueryKeys.unreadCount(userSysId) });
    },
  });
};

